import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { FullCalendarModule } from '@fullcalendar/angular';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { CalendarOptions, DatesSetArg, EventClickArg } from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import { DateTime } from 'luxon';
import { CalendarService } from './services/calendar.service';
import { Router } from '@angular/router';
import { Sequences } from '@shared/models/activity-summary/sequences';
import { SkeletonModule } from 'primeng/skeleton';
import { tap } from 'rxjs';
import { FlightDetailDialogComponent } from '../flight-detail/flight-detail-dialog/flight-detail-dialog.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FlightTotalsService } from '../services/flight-totals.service';


@Component({
  selector: 'app-flights-calendar',
  standalone: true,
  imports: [
    CommonModule,
    FullCalendarModule,
    SkeletonModule
  ],
  templateUrl: './flights-calendar.component.html',
  styleUrl: './flights-calendar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlightsCalendarComponent implements OnInit {
  @ViewChild('calendar') calendar?: FullCalendarComponent;
  ref: DynamicDialogRef | undefined;
  lastUpdated?: string;
  specifiedEmpNum = '';
  isHolidayPayMonth = false;

  calendar$ = this.calendarService.calendar$.pipe(
    tap( (calendar) => {
      const timestamp = calendar.response?.timestamp ? calendar.response.timestamp.toISO() : '';
      this.lastUpdated = this.calendarService.loadLastUpdted(timestamp || '');
    })
  );  
  
  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    height: 'auto',
    handleWindowResize: true,
    unselectAuto: false,
    fixedWeekCount: false,
    showNonCurrentDates: true,
    titleFormat: {
      month: 'long',
      year: 'numeric',
    },
    headerToolbar: {
      start: 'prev, prevYear',
      center: 'title',
      end: 'nextYear, next' 
    },
    datesSet: this.onMonthChanged.bind(this),
    eventClick: this.onEventClick.bind(this),
  };


  constructor(
    private calendarService: CalendarService,
    private router: Router,
    private dialogService: DialogService,
    private flightTotalsService: FlightTotalsService
  ) {}

  //convert to date object then string...
  ngOnInit(): void {
    const initializedDate = this.calendarService.getIntialDate();
    const initialDate = this.calendarService.createDateStringFrom(initializedDate);
    const date = DateTime.fromISO(initialDate).plus({ days: 15 });
    this.setHolidayPayMonth( { year: date.year, period: date.month });
    
    this.calendarService.setSelectedMonth(initializedDate); 
    this.calendarOptions = {
      ...this.calendarOptions,
      initialDate     
    }
  }

  onEventClick(event: EventClickArg): void {
    const to = 'cal-sequence-detail';
    const sequence = this.reversedLegsForSequence(event.event.extendedProps['sequence']);

    sessionStorage.setItem('to', JSON.stringify(to));
    sessionStorage.setItem('sequence', JSON.stringify(sequence));

    this.router.navigate(['sequence-detail']);
  }

  reversedLegsForSequence(sequence: Sequences): Sequences {
    sequence;
    for ( const duty of sequence.dutyPeriodSummaries.reverse()) {
      duty.legs.reverse();
    }
    return sequence;
  }

  // Updates selectedMonth observable triggering events update.
  onMonthChanged(dateRange: DatesSetArg): void {
    const date = DateTime.fromISO(dateRange.startStr).plus({ days: 15 });
    const dateObject = {
      year: date.year,
      period: date.month
    }
    this.calendarService.setSelectedMonth(dateObject);
    this.setHolidayPayMonth(dateObject);
    

    if (this.calendar) {
      const api = this.calendar.getApi();
      api.getEvents();
    }
  }

  onHelpClicked(): void {
    this.ref = this.dialogService.open(FlightDetailDialogComponent, {
          data: 3,
          header: 'Calendar',
          width: '50vw',
          modal: true,
          dismissableMask: true,
          breakpoints: {
            '500px': '98vw',
          },
          contentStyle: { 
            'background-color': 'var(--surface-0)',
          }
        });
  }

  onRefreshClicked(): void {
   this.calendarService.forceRefresh();
   this.flightTotalsService._isRefresh.next(true);
  }

  setHolidayPayMonth(dateObject: { year: number; period: number }): void {
    // Set isHolidayPayMonth flag.
    const month = dateObject.period;
    if (
      month === 1 ||
      month === 5 ||
      month === 7 ||
      month === 11 ||
      month === 12
    ) {
      this.isHolidayPayMonth = true;
    } 
    else {
      this.isHolidayPayMonth = false;
    }
  }
}
